import { assetFetchInit, assetUrl } from './assetUrl.js';

/**
 * iOS-13-safe Web Audio sound manager.
 *
 * Why not HTMLAudio? On iOS, <audio> elements have very poor concurrent-playback
 * behavior (only one stream can effectively run; latency is high; and they're
 * subject to aggressive throttling when the page is backgrounded). Web Audio
 * solves all three: a single AudioContext can mix many AudioBufferSourceNodes
 * with sub-frame latency, and decoded buffers replay instantly with no GC churn.
 *
 * iOS specifics handled here:
 *   - `webkitAudioContext` fallback for iOS 12/13 Safari.
 *   - Suspended-by-default AudioContext is resumed inside a real user gesture
 *     (pointerdown/touchstart/touchend/mousedown/keydown — capture phase).
 *   - Silent-buffer "unlock" trick still needed on iOS 12/13 to enable playback.
 *   - `decodeAudioData` is wrapped to support both promise + callback forms
 *     (iOS 12 only supports the callback form).
 *   - Sequential decode during preload to avoid the memory spike that crashes
 *     mobile Safari when many MP3s decode in parallel.
 *   - `visibilitychange` resume so the context wakes up after backgrounding.
 *
 * Every play() creates a throwaway AudioBufferSourceNode — these are GC-cheap
 * and explicitly designed for high-frequency triggering. A short per-key
 * throttle prevents accidental machine-gun overlap on rapid taps / reel stops.
 */

const SOUND_PATHS = {
  bg: 'assets/sounds/music/background_music_v2.mp3',
  spinStart: 'assets/sounds/reel/reel_start_button.mp3',
  spinStop: 'assets/sounds/reel/reel_stop_button.mp3',
  reelSpin: 'assets/sounds/reel/reel_spin_sound.mp3',
  reelDefaultStop: 'assets/sounds/reel/reel_default_stop.mp3',
  reelStopper: 'assets/sounds/reel/reel_stopper.mp3',
  winFrame: 'assets/sounds/reel/win_frame_sound.mp3',
  expandingWilds: 'assets/sounds/reel/expanding_wilds.mp3',
  bigWin: 'assets/sounds/popups/big_win_popup.mp3',
  hugeWin: 'assets/sounds/popups/huge_win_popup.mp3',
  megaWin: 'assets/sounds/popups/mega_win_popup.mp3',
  epicWin: 'assets/sounds/popups/epic_win_popup.mp3',
  popupOut: 'assets/sounds/popups/popup_out.mp3',
  win: 'assets/sounds/Symbols/win.mp3',
  wild: 'assets/sounds/Symbols/WIld.mp3',
  scatter: 'assets/sounds/Symbols/Scatter.mp3',
  coin: 'assets/sounds/Symbols/Coin.mp3',
  uiClick: 'assets/sounds/ui/ui_click.mp3',
  uiError: 'assets/sounds/ui/ui_error.mp3',
  uiPopupOpen: 'assets/sounds/ui/ui_popup_open.mp3',
  uiPopupClose: 'assets/sounds/ui/ui_popup_close.mp3',
  uiSpinSpeed: 'assets/sounds/ui/ui_spin_speed.mp3',
};

/** Background music bus — kept separate from all one-shots. */
const MUSIC_BUS_VOLUME = 0.35;

/** Shared bus for every non-music sound (UI, reels, symbols, win popups). */
const ONESHOT_BUS_VOLUME = 0.8;

/** Default per-clip gain on the one-shot bus (1 = full bus level). */
const DEFAULT_ONESHOT_GAIN = 1;

/**
 * Per-key multipliers on top of {@link ONESHOT_BUS_VOLUME}. Omit = default.
 * Start/stop button MP3s are mastered hotter — pull them down to match the mix.
 */
const SOUND_VOLUMES = {
  spinStart: 0.55,
  spinStop: 0.55,
  reelSpin: 0.65,
  reelDefaultStop: 1,
  reelStopper: 1.25,
  winFrame: 0.85,
  win: 0.85,
  wild: 0.85,
  scatter: 0.45,
  coin: 0.65,
};

/** Default minimum gap (seconds) between two retriggers of the same key. */
const DEFAULT_MIN_GAP = 0.04;

/** Win popup one-shots — stopped when the celebration tier animation changes. */
const WIN_CELEBRATION_SOUND_KEYS = new Set(['bigWin', 'hugeWin', 'megaWin', 'epicWin']);

/** iOS / iPadOS Safari and in-app WebViews (Instagram, Telegram, etc.). */
function isIOSWebKit() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return (
    /iP(hone|ad|od)/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

class SoundManager {
  constructor() {
    /** @type {AudioContext | null} */
    this.ctx = null;
    /** @type {Record<string, AudioBuffer>} */
    this.buffers = Object.create(null);
    this.muted = false;
    this.unlocked = false;
    /** @type {GainNode | null} */
    this._masterGain = null;
    /** @type {GainNode | null} */
    this._musicGain = null;
    /** @type {GainNode | null} */
    this._oneShotGain = null;
    /** @type {AudioBufferSourceNode | null} */
    this._bgSource = null;
    this._bgPlaying = false;
    /** @type {Set<AudioBufferSourceNode>} Active non-music sources, tracked so a
     *  new spin can hard-stop any lingering one-shots (win tier audio, symbol
     *  chimes, popup transitions) without touching the bg music loop. */
    this._activeSources = new Set();
    /** Logical key per source — must use WeakMap: Safari/WebKit often ignores
     *  expando properties on native AudioBufferSourceNode, so `src._sndKey`
     *  was always undefined and {@link stopAllSfx} killed `spinStop` too. */
    this._sourcePlayKey = new WeakMap();
    /** @type {Record<string, number>} ctx.currentTime of last play per key (throttle). */
    this._lastPlayedAt = Object.create(null);
    this._unlockListeners = null;
    this._visListener = null;
    /** @type {boolean | null} cached once per page load */
    this._isIOSWebKit = null;
    /** Raw MP3 bytes fetched before AudioContext exists (autoplay policy). */
    this._rawAudio = Object.create(null);
    if (typeof window !== 'undefined') {
      this._installUnlock();
      this._installVisibility();
    }
  }

  _onIOSWebKit() {
    if (this._isIOSWebKit == null) this._isIOSWebKit = isIOSWebKit();
    return this._isIOSWebKit;
  }

  /**
   * Call synchronously from a real UI pointer handler (spin, chips, settings).
   * Window-level unlock listeners usually cover this; explicit calls keep resume
   * inside the user-gesture chain when Pixi consumes the event first.
   */
  onUserGesture() {
    this._unlock(true);
  }

  _AudioContextCtor() {
    if (typeof window === 'undefined') return null;
    return window.AudioContext || window.webkitAudioContext || null;
  }

  _ensureCtx() {
    if (this.ctx) return this.ctx;
    const Ctor = this._AudioContextCtor();
    if (!Ctor) return null;
    try {
      this.ctx = new Ctor({ latencyHint: 'interactive' });
    } catch (_e) {
      try {
        this.ctx = new Ctor();
      } catch (_e2) {
        return null;
      }
    }
    this._masterGain = this.ctx.createGain();
    this._masterGain.gain.value = this.muted ? 0 : 1;
    this._masterGain.connect(this.ctx.destination);
    this._musicGain = this.ctx.createGain();
    this._musicGain.gain.value = MUSIC_BUS_VOLUME;
    this._musicGain.connect(this._masterGain);
    this._oneShotGain = this.ctx.createGain();
    this._oneShotGain.gain.value = ONESHOT_BUS_VOLUME;
    this._oneShotGain.connect(this._masterGain);
    return this.ctx;
  }

  _installUnlock() {
    /* No `click` / `touchend` — host apps often synthesize those in iframes and
     * Chrome logs "AudioContext was not allowed to start" when resume() runs
     * outside a real gesture. */
    const events = ['pointerdown', 'touchstart', 'mousedown', 'keydown'];
    const handler = (e) => {
      if (e && e.isTrusted === false) return;
      this._unlock(true);
    };
    /** capture: react before app handlers, passive: don't block scrolling. */
    const opts = { capture: true, passive: true };
    for (let i = 0; i < events.length; i++) {
      window.addEventListener(events[i], handler, opts);
    }
    this._unlockListeners = { events: events, handler: handler, opts: opts };
  }

  _detachUnlock() {
    if (!this._unlockListeners) return;
    const u = this._unlockListeners;
    for (let i = 0; i < u.events.length; i++) {
      window.removeEventListener(u.events[i], u.handler, u.opts);
    }
    this._unlockListeners = null;
  }

  _installVisibility() {
    const onVisibility = () => {
      if (!this.ctx) return;
      if (document.visibilityState === 'visible') {
        this._scheduleResume();
      } else {
        this._suspendCtx();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    /* iOS Safari also dispatches `pagehide` when entering bf-cache and `blur`
     * when switching apps — belt-and-suspenders so audio never bleeds through.
     * Skip `blur`/`focus` on iOS: they fire spuriously while the page is still
     * visible (WebViews, keyboard, scroll) and suspend audio without a reliable
     * way to resume until the next tap. */
    window.addEventListener('pagehide', () => this._suspendCtx());
    if (!this._onIOSWebKit()) {
      window.addEventListener('blur', () => this._suspendCtx());
      window.addEventListener('focus', () => this._scheduleResume());
    }
  }

  _isPageActive() {
    if (typeof document === 'undefined') return false;
    if (document.visibilityState && document.visibilityState !== 'visible') return false;
    /* document.hasFocus() is unreliable on iOS Safari / WebViews — it often
     * returns false while the user is actively tapping the canvas, which would
     * silence every reel-land and win SFX. Visibility is the safe gate there. */
    if (!this._onIOSWebKit() && typeof document.hasFocus === 'function' && !document.hasFocus()) {
      return false;
    }
    return true;
  }

  /**
   * Resume after a short debounce. When returning from another app, the
   * browser fires `focus` / `visibilitychange visible` on the previously
   * active game tab even if the user immediately switches to a different
   * tab. Without a debounce we'd hear a brief blast of music between the
   * resume and the subsequent suspend; this delays the resume long enough
   * for an incoming suspend to cancel it.
   */
  _scheduleResume() {
    /* Never call ctx.resume() here — embeds / background tabs are not user
     * gestures and Chrome warns loudly. Audio resumes on the next real tap. */
  }

  _needsResume() {
    if (!this.ctx) return false;
    const s = this.ctx.state;
    return s === 'suspended' || s === 'interrupted';
  }

  _suspendCtx() {
    if (this._resumeTimer) {
      clearTimeout(this._resumeTimer);
      this._resumeTimer = null;
    }
    if (!this.ctx) return;
    if (this.ctx.state === 'running') {
      try {
        const r = this.ctx.suspend();
        if (r && typeof r.then === 'function') r.then(undefined, () => {});
      } catch (_e) {
        /* ignore */
      }
    }
  }

  _unlock(fromGesture) {
    if (!fromGesture) return;
    const ctx = this._ensureCtx();
    if (!ctx) return;
    if (this._needsResume()) {
      try {
        const r = ctx.resume();
        if (r && typeof r.then === 'function') {
          void r.then(() => this._completeUnlock()).catch(() => {});
          return;
        }
      } catch (_e) {
        return;
      }
      if (ctx.state !== 'running') return;
    }
    void this._completeUnlock();
  }

  _completeUnlock() {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return;
    try {
      const buf = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      if (src.start) src.start(0);
      else if (src.noteOn) src.noteOn(0);
    } catch (_e) {
      /* ignore */
    }
    if (!this.unlocked) {
      this.unlocked = true;
      if (!this._onIOSWebKit()) this._detachUnlock();
      void this._decodeAllPending().then(() => {
        if (!this.muted && this.buffers.bg) this._startBgMusic();
      });
    } else if (!this.muted && this.buffers.bg && !this._bgPlaying) {
      this._startBgMusic();
    }
  }

  /**
   * Fetch + decode every sound. Sequential to keep iOS memory steady; the
   * total payload is small enough that overall latency is fine.
   * Safe to call multiple times; only the first kicks off work.
   */
  preload() {
    if (this._preloadPromise) return this._preloadPromise;
    const keys = Object.keys(SOUND_PATHS);
    let chain = Promise.resolve();
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      chain = chain.then(() => this._fetchOne(key));
    }
    this._preloadPromise = chain.then(() => {
      if (this.unlocked && !this.muted && this.buffers.bg) {
        this._startBgMusic();
      }
    });
    return this._preloadPromise;
  }

  _fetchOne(key) {
    if (this.buffers[key] || this._rawAudio[key]) return Promise.resolve();
    const url = assetUrl('/' + SOUND_PATHS[key]);
    return fetch(url, assetFetchInit)
      .then((res) => {
        if (!res.ok) throw new Error('http ' + res.status + ' for ' + url);
        return res.arrayBuffer();
      })
      .then((ab) => {
        this._rawAudio[key] = ab;
      })
      .catch((err) => {
        console.warn('SoundManager: failed to load ' + key + ' (' + url + ')', err);
      });
  }

  _decodeAllPending() {
    const keys = Object.keys(SOUND_PATHS);
    let chain = Promise.resolve();
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (this.buffers[key]) continue;
      const ab = this._rawAudio[key];
      if (!ab) {
        chain = chain.then(() => this._fetchOne(key).then(() => this._decodeKey(key)));
      } else {
        chain = chain.then(() => this._decodeKey(key));
      }
    }
    return chain;
  }

  _decodeKey(key) {
    const ab = this._rawAudio[key];
    if (!ab || !this.ctx) return Promise.resolve();
    return this._decode(ab).then((buf) => {
      if (buf) this.buffers[key] = buf;
      delete this._rawAudio[key];
    });
  }

  _loadOne(key) {
    return this._fetchOne(key).then(() => {
      if (this.unlocked && this.ctx) return this._decodeKey(key);
    });
  }

  _decode(arrayBuffer) {
    return new Promise((resolve) => {
      if (!this.ctx) return resolve(null);
      let settled = false;
      const ok = (b) => {
        if (settled) return;
        settled = true;
        resolve(b || null);
      };
      const fail = () => {
        if (settled) return;
        settled = true;
        resolve(null);
      };
      try {
        const p = this.ctx.decodeAudioData(arrayBuffer, ok, fail);
        if (p && typeof p.then === 'function') {
          p.then(ok, fail);
        }
      } catch (_e) {
        fail();
      }
    });
  }

  /** Master mute. Smoothly ramps to avoid clicks. */
  setMuted(mute) {
    this.muted = !!mute;
    if (this._masterGain && this.ctx) {
      const t = this.ctx.currentTime;
      const target = this.muted ? 0 : 1;
      try {
        this._masterGain.gain.cancelScheduledValues(t);
        this._masterGain.gain.setValueAtTime(this._masterGain.gain.value, t);
        this._masterGain.gain.linearRampToValueAtTime(target, t + 0.05);
      } catch (_e) {
        this._masterGain.gain.value = target;
      }
    }
    if (this.muted) {
      this._stopBgMusic();
    } else if (this.unlocked && this.buffers.bg) {
      this._startBgMusic();
    }
  }

  isMuted() {
    return this.muted;
  }

  /**
   * Trigger a one-shot sound. No-ops cleanly if muted, missing, or pre-unlock.
   * @param {string} key key in SOUND_PATHS
   * @param {{ channel?: 'sfx' | 'win' | 'music', volume?: number, minGap?: number, allowOverlap?: boolean }} [opts]
   *   `channel` is ignored for one-shots (all use the shared one-shot bus). Music
   *   only plays via the background loop, not `play('bg')`.
   */
  play(key, opts) {
    if (!this.buffers[key] && SOUND_PATHS[key]) {
      void this._loadOne(key).then(() => this._playOneShot(key, opts));
      return;
    }
    this._playOneShot(key, opts);
  }

  /** Hard-stop any in-flight win popup tier sound (big / huge / mega / epic). */
  stopWinCelebrationSounds() {
    if (this._activeSources.size === 0) return;
    const sources = Array.from(this._activeSources);
    for (let i = 0; i < sources.length; i++) {
      const src = sources[i];
      const k = this._sourcePlayKey.get(src);
      if (!WIN_CELEBRATION_SOUND_KEYS.has(k)) continue;
      this._activeSources.delete(src);
      try { src.onended = null; } catch (_e) { /* ignore */ }
      try { src.stop(0); } catch (_e) { /* ignore */ }
      try { src.disconnect(); } catch (_e) { /* ignore */ }
    }
  }

  /**
   * Switch win popup audio to match the tier animation that just started.
   * Cuts any previous tier popup sound before playing the new one.
   * @param {'bigWin' | 'hugeWin' | 'megaWin' | 'epicWin'} key
   */
  playWinCelebrationTierSound(key) {
    if (!WIN_CELEBRATION_SOUND_KEYS.has(key)) return;
    if (this.muted || !this._isPageActive()) return;
    this.stopWinCelebrationSounds();
    this._playOneShot(key, { allowOverlap: true });
  }

  /**
   * @param {string} key
   * @param {{ channel?: 'sfx' | 'win' | 'music', volume?: number, minGap?: number, allowOverlap?: boolean }} [opts]
   * @param {() => void} [onEnded]
   */
  _playOneShot(key, opts, onEnded) {
    if (this.muted) {
      onEnded?.();
      return;
    }
    if (key === 'bg') {
      onEnded?.();
      return;
    }
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') {
      onEnded?.();
      return;
    }
    const buffer = this.buffers[key];
    if (!buffer) {
      onEnded?.();
      return;
    }
    /* Stray timer in a backgrounded tab? Don't start sound and don't resume
     * the context — otherwise an autoplay / GSAP callback firing while we
     * are on another tab would re-wake the audio engine. */
    if (!this._isPageActive()) {
      onEnded?.();
      return;
    }

    const startSource = () => {
      const now = ctx.currentTime;
      const minGap = opts && opts.minGap != null ? opts.minGap : DEFAULT_MIN_GAP;
      if (!(opts && opts.allowOverlap)) {
        const last = this._lastPlayedAt[key] || 0;
        if (now - last < minGap) return;
        this._lastPlayedAt[key] = now;
      }
      const dest = this._oneShotGain;
      try {
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        const override = opts && opts.volume;
        const vol =
          override != null && Number.isFinite(override)
            ? override
            : SOUND_VOLUMES[key] != null
              ? SOUND_VOLUMES[key]
              : DEFAULT_ONESHOT_GAIN;
        const g = ctx.createGain();
        g.gain.value = vol;
        src.connect(g);
        g.connect(dest);
        this._sourcePlayKey.set(src, key);
        this._activeSources.add(src);
        src.onended = () => {
          this._activeSources.delete(src);
          onEnded?.();
        };
        if (src.start) src.start(0);
        else if (src.noteOn) src.noteOn(0);
      } catch (_e) {
        onEnded?.();
      }
    };

    startSource();
  }

  /**
   * Hard-stop every currently playing one-shot, leaving the bg music loop alone.
   * Used when a new spin begins so leftover audio from the
   * previous round (popup tails, symbol chimes, win tier hits) cuts cleanly.
   *
   * `spinStop`, `reelDefaultStop`, and `reelStopper` are always exempt — the stop
   * button click and per-reel landings should ring out; cutting them when the next spin starts
   * sounds broken on iOS (native nodes did not retain expando keys before
   * {@link #_sourcePlayKey}).
   * Callers may pass extra `except` keys for additional carve-outs.
   *
   * @param {{ except?: string[] }} [opts]
   */
  stopAllSfx(opts) {
    if (this._activeSources.size === 0) return;
    const extra = opts && opts.except;
    const sources = Array.from(this._activeSources);
    for (let i = 0; i < sources.length; i++) {
      const src = sources[i];
      const k = this._sourcePlayKey.get(src);
      if (k === 'spinStop' || k === 'reelDefaultStop' || k === 'reelStopper') continue;
      if (extra && extra.indexOf(k) >= 0) continue;
      this._activeSources.delete(src);
      try { src.onended = null; } catch (_e) { /* ignore */ }
      try { src.stop(0); } catch (_e) { /* ignore */ }
      try { src.disconnect(); } catch (_e) { /* ignore */ }
    }
  }

  _startBgMusic() {
    if (this._bgPlaying || !this.buffers.bg || this.muted || !this._isPageActive()) return;
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return;
    try {
      const src = ctx.createBufferSource();
      src.buffer = this.buffers.bg;
      src.loop = true;
      src.connect(this._musicGain);
      if (src.start) src.start(0);
      else if (src.noteOn) src.noteOn(0);
      this._bgSource = src;
      this._bgPlaying = true;
    } catch (_e) {
      /* ignore */
    }
  }

  _stopBgMusic() {
    if (!this._bgSource) return;
    try { this._bgSource.stop(0); } catch (_e) { /* ignore */ }
    try { this._bgSource.disconnect(); } catch (_e) { /* ignore */ }
    this._bgSource = null;
    this._bgPlaying = false;
  }
}

export const sound = new SoundManager();
